import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type { AppetiteType } from '@risksmart-app/domain/src/types/consts/index';
import type {
  InsertAppetiteMutation,
  InsertAppetiteMutationVariables,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { InsertAppetiteDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

// Base fields shared by all appetite types
interface AppetiteBaseInput {
  ParentIds: string[];
  Statement?: string | null;
  EffectiveDate?: string | null;
  CustomAttributeData?: CustomAttributeData | null;
}

type InsertAppetiteTRPCInput = AppetiteBaseInput &
  (
    | {
        AppetiteType: typeof AppetiteType.Risk;
        LowerAppetite?: number | null;
        UpperAppetite?: number | null;
      }
    | {
        AppetiteType: typeof AppetiteType.Impact;
        ImpactAppetite: number;
        ImpactId: string;
      }
    | {
        AppetiteType: typeof AppetiteType.Likelihood;
        LikelihoodAppetite?: number | null;
      }
  );

export const useInsertAppetite = createMutationHook({
  name: 'insertAppetite',
  isIdempotentCreate: true,
  mutationOptions: (trpc, onSuccess, requestContext) =>
    trpc.frontend.appetite.insert.mutationOptions({
      onSuccess,
      trpc: { context: requestContext },
    }),
  // Map GraphQL variables to tRPC input.
  // ParentIds is typed as `string | string[]` by GraphQL codegen (inline
  // variable coercion), but callers always provide `string[]` — cast
  // accordingly.
  mapInput: (variables: InsertAppetiteMutationVariables) =>
    ({
      ParentIds: variables.ParentIds as string[],
      AppetiteType: variables.AppetiteType!,
      Statement: variables.Statement,
      EffectiveDate: variables.EffectiveDate,
      LowerAppetite: variables.LowerAppetite,
      UpperAppetite: variables.UpperAppetite,
      ImpactAppetite: variables.ImpactAppetite,
      LikelihoodAppetite: variables.LikelihoodAppetite,
      ImpactId: variables.ImpactId,
      CustomAttributeData: variables.CustomAttributeData,
    }) as InsertAppetiteTRPCInput,
  invalidate: (trpc) => [
    // COI-889: router-level key — the hand-listed set missed
    // `getAppetitesGroupedByImpact` (both siblings have it) and `getAppetites`.
    trpc.frontend.appetite.pathKey(),
    // COI-471 parity: Apollo twin evicts the `risk` field (appetites
    // surface on the linked risk).
    trpc.frontend.risk.pathKey(),
  ],
  mapResponse: (result: { Id: string }): InsertAppetiteMutation => ({
    insertChildAppetite: {
      __typename: 'IdOutput',
      Id: result.Id,
    },
  }),
  graphqlDocument: InsertAppetiteDocument,
  graphqlVariables: (variables: InsertAppetiteMutationVariables) => ({
    ...variables,
  }),
  evictFields: ['appetite', 'appetite_aggregate', 'risk_aggregate'],
});
